import { cn } from '@/lib/utils'

type Props = {
  description: string
  className?: string
  /** タイムラインカード等の狭い領域向けに文字サイズ・余白を詰める */
  compact?: boolean
}

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue }

const tryParseJson = (value: string): { ok: true; data: JsonValue } | { ok: false } => {
  const trimmed = value.trim()
  if (!trimmed) return { ok: false }
  const first = trimmed[0]
  if (first !== '{' && first !== '[') return { ok: false }
  try {
    return { ok: true, data: JSON.parse(trimmed) as JsonValue }
  } catch {
    return { ok: false }
  }
}

// __ で始まる内部マーカーキー（__converted__ / __transcript__ 等）は表示から除外する

const isIsoDate = (v: string): boolean => /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)

const formatPrimitive = (value: string | number | boolean | null): string => {
  if (value === null) return 'null'
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'string' && isIsoDate(value)) {
    const d = new Date(value)
    if (!Number.isNaN(d.getTime())) return d.toLocaleString('ja-JP')
  }
  return String(value)
}

const JsonNode = ({ value, depth = 0, compact = false }: { value: JsonValue; depth?: number; compact?: boolean }) => {
  if (value === null || typeof value !== 'object') {
    return <span className="break-all text-gray-100">{formatPrimitive(value)}</span>
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-gray-400">[]</span>
    }
    return (
      <ul className={cn('list-disc space-y-1 pl-4', depth > 0 && 'mt-1')}>
        {value.map((item, i) => (
          <li key={i} className="text-gray-100">
            <JsonNode value={item} depth={depth + 1} compact={compact} />
          </li>
        ))}
      </ul>
    )
  }

  const entries = Object.entries(value).filter(([key]) => !key.startsWith('__'))
  if (entries.length === 0) {
    return <span className="text-gray-400">{'{}'}</span>
  }

  return (
    <dl className={cn('space-y-1', depth > 0 && 'mt-1 border-l border-white/10 pl-3')}>
      {entries.map(([key, v]) => {
        const isLeaf = v === null || typeof v !== 'object'
        return (
          <div key={key} className={cn(isLeaf ? 'flex flex-wrap gap-x-2' : 'space-y-1')}>
            <dt className={cn('font-semibold text-gray-300', compact ? 'text-[10px]' : 'text-xs')}>{key}</dt>
            <dd className={cn('text-gray-100', compact ? 'text-xs' : 'text-sm')}>
              <JsonNode value={v} depth={depth + 1} compact={compact} />
            </dd>
          </div>
        )
      })}
    </dl>
  )
}

/**
 * Product / Episode / 読書履歴の description を表示するコンポーネント。
 * 値が JSON オブジェクト/配列としてパース可能な場合はキー・値ツリーとして描画し、
 * そうでなければプレーンテキストとして表示する。
 */
export const DescriptionView = ({ description, className, compact = false }: Props) => {
  const parsed = tryParseJson(description)

  if (!parsed.ok) {
    return (
      <div className={cn('whitespace-pre-wrap text-gray-200', compact ? 'text-xs' : 'text-sm', className)}>
        {description}
      </div>
    )
  }

  return (
    <div className={cn('rounded-md bg-white/5', compact ? 'p-2 text-xs' : 'p-3 text-sm', className)}>
      <JsonNode value={parsed.data} compact={compact} />
    </div>
  )
}
